/**
 * Event landing-page template — 03-gala ("Black-Tie Benefit Gala").
 *
 * Distinct CraftJS home-page document for the `gala` theme. Cloned from the
 * proven `webinar` template (same structure: section node factories, the main
 * `gala(td)` doc, plus `galaAppbar(td)` / `galaFooter(td)`), restyled to the
 * dark-luxury-gold look — near-black surfaces (#0A0A0A/#141414), champagne-gold
 * accent (#C9A24B, registry-synced), Didot serif headers, cream body, rounded-none.
 * Authored from TEMPLATES - LANDINPAGES/03-gala/sections/*.html.
 *
 * Convention (mirrors webinar):
 *  - Section-level marketing copy goes through td() keys (`gal*` prefix).
 *  - Repetitive structured child data (agenda rows, speakers, FAQ, …) also
 *    goes through td() keys (Wave 3b): the *_DATA arrays are functions of td.
 *    Only fictitious proper names, bare numbers and asset URLs stay inline.
 *  - Global colors/fonts come from galaPreset (applied via the theme); this is
 *    a DARK theme, so every section sets its own dark surface + light text.
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import { direction } from "@/lib/designDirections/directions";
import type { WebsiteThemeStylePreset } from "@/types/WebsiteTheme";
import {
  SAMPLE_SPEAKER_IMAGES,
  SAMPLE_SPONSOR_LOGOS,
} from "@/lib/website-builder/sampleContent";

// Gala-specific asset namespace (seeded under <CDN>/gala/…)
const W = `${A}/shared/templates/03-gala`;

// WS6: gala palette is registry-synced — the champagne golds + base surface
// come from the shared design-directions canon (alias names kept).
const GOLD = direction("gala").primary; // champagne-gold accent (#C9A24B)
const GOLD_SOFT = direction("gala").accent; // pale champagne (#F4E7B8, headlines on dark)
const CREAM = "#f5f0e8"; // cream body text on dark
const MUTE = "#a0978a"; // muted body on dark
const BG = direction("gala").background; // near-black base surface (#0A0A0A)
const SURFACE = "#141414"; // raised dark surface
const WHITE = "#ffffff";
const SERIF = "Didot, Bodoni, 'Bodoni MT', Georgia, serif";
const SANS = "Inter, 'Helvetica Neue', Arial, sans-serif";

// Speaker/testimonial faces come from the shared sample rotation
// (lib/website-builder/sampleContent); the template's own `<family>/*`
// art stays what it is: heroes, details and gallery photography.
const LOGO = `${A}/shared/logo.svg`;

// ── Inline child data (content rows resolved through td, Wave 3b) ─────

const NUMBERS_DATA = (td: TranslationFn) => [
  { num: "<b>MMXXVI</b>", desc: td("galNum1Desc") },
  { num: "<b>24</b>", desc: td("galNum2Desc") },
  { num: "<b>288</b>", desc: td("galNum3Desc") },
  { num: "<b>120k €</b>", desc: td("galNum4Desc") },
];

const AGENDA_DATA = (td: TranslationFn) => [
  { time: `<b>${td("galAgendaRow1Time")}</b>`, title: td("galAgendaRow1Title"), who: td("galAgendaRow1Who"), desc: td("galAgendaRow1Desc") },
  { time: `<b>${td("galAgendaRow2Time")}</b>`, title: td("galAgendaRow2Title"), who: td("galAgendaRow2Who"), desc: td("galAgendaRow2Desc") },
  { time: `<b>${td("galAgendaRow3Time")}</b>`, title: td("galAgendaRow3Title"), who: td("galAgendaRow3Who"), desc: td("galAgendaRow3Desc") },
  { time: `<b>${td("galAgendaRow4Time")}</b>`, title: td("galAgendaRow4Title"), who: td("galAgendaRow4Who"), desc: td("galAgendaRow4Desc") },
  { time: `<b>${td("galAgendaRow5Time")}</b>`, title: td("galAgendaRow5Title"), who: td("galAgendaRow5Who"), desc: td("galAgendaRow5Desc") },
];

const SPEAKER_DATA = (td: TranslationFn) => [
  { name: td("galSpeaker1Name"), role: td("galSpeaker1Role"), img: "0" },
  { name: td("galSpeaker2Name"), role: td("galSpeaker2Role"), img: "1" },
  { name: td("galSpeaker3Name"), role: td("galSpeaker3Role"), img: "2" },
  { name: "Dr. Cornelia Reinhardt", role: td("galSpeaker4Role"), img: "3" },
  { name: "Hon. Maximilian Leutner", role: td("galSpeaker5Role"), img: "4" },
  { name: "Sofia von Eberstein", role: td("galSpeaker6Role"), img: "5" },
];

const TESTIMONIAL_DATA = (td: TranslationFn) => [
  { quote: td("galTestimonial1Quote"), name: "Dr. Elisabeth Hartmann", role: td("galTestimonial1Role"), img: "0" },
  { quote: td("galTestimonial2Quote"), name: "Friedrich von Lindeberg", role: td("galTestimonial2Role"), img: "1" },
  { quote: td("galTestimonial3Quote"), name: "Sofia von Eberstein", role: td("galTestimonial3Role"), img: "2" },
];

const BLOG_DATA = (td: TranslationFn) => [
  { topic: `<b>${td("galBlog1Topic")}</b>`, content: td("galBlog1Content") },
  { topic: `<b>${td("galBlog2Topic")}</b>`, content: td("galBlog2Content") },
  { topic: `<b>${td("galBlog3Topic")}</b>`, content: td("galBlog3Content") },
];

const FAQ_DATA = (td: TranslationFn) => [
  { q: `<b>${td("galFaq1Question")}</b>`, a: td("galFaq1Answer") },
  { q: `<b>${td("galFaq2Question")}</b>`, a: td("galFaq2Answer") },
  { q: `<b>${td("galFaq3Question")}</b>`, a: td("galFaq3Answer") },
  { q: `<b>${td("galFaq4Question")}</b>`, a: td("galFaq4Answer") },
  { q: `<b>${td("galFaq5Question")}</b>`, a: td("galFaq5Answer") },
  { q: `<b>${td("galFaq6Question")}</b>`, a: td("galFaq6Answer") },
];

// ── Gala-specific child factories ─────────────────────────────────────

function numbersChild(parent: string, i: number, td: TranslationFn): N {
  const d = NUMBERS_DATA(td)[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(parent: string, i: number, td: TranslationFn): N {
  const d = AGENDA_DATA(td)[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: d.time, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.who, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function speakerChild(parent: string, i: number, td: TranslationFn): N {
  const d = SPEAKER_DATA(td)[i];
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
      icons: [{ icon: "IconBrandLinkedin" }, { icon: "IconMail" }], isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function testimonialChild(parent: string, i: number, td: TranslationFn): N {
  const d = TESTIMONIAL_DATA(td)[i];
  return {
    type: { resolvedName: "TestimonialsChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPONSOR_LOGOS[i % SAMPLE_SPONSOR_LOGOS.length].src,
      imageUrlBottom: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length],
      isVisibleImageTop: false, imageBottomBackground: "transparent", isVisibleImageBottom: false,
      isVisibleContent: true, textContent: d.quote, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
    },
    displayName: "TestimonialsChild", custom: { icon: "IconThumbUp" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function blogChild(parent: string, i: number, td: TranslationFn): N {
  const d = BLOG_DATA(td)[i];
  return {
    type: { resolvedName: "BlogChild" }, isCanvas: false,
    props: {
      iconAlignment: "left", isVisible: true,
      isVisibleTopic: true, textTopic: d.topic, fontSizeTopic: "text-lg", textAlignTopic: "left",
      isVisibleContent: true, textContent: d.content, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleButton: false, size: "small", text: td("learnMore"), underline: false, border: true,
    },
    displayName: "BlogChild", custom: { icon: "IconArticle" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number, td: TranslationFn): N {
  const d = FAQ_DATA(td)[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

const GALLERY_IMG = [
  `${W}/detail-champagne.jpg`, `${W}/detail-gold-cutlery.jpg`, `${W}/detail-napkin.jpg`,
  `${W}/detail-place-card.jpg`, `${W}/hero-zusage.jpg`, `${W}/hero-save-the-date.jpg`,
];

function galleryImage(parent: string, i: number): N {
  return {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: GALLERY_IMG[i % GALLERY_IMG.length], isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Template ──────────────────────────────────────────────────────────

export const gala = (td: TranslationFn): string => {
  const B = "gal_body";

  // Top-level section ids (body order)
  const HERO = "gal_hero";
  const NUMBERS = "gal_numbers";
  const AGENDA = "gal_agenda";
  const SPEAKER = "gal_speaker";
  const TEXT = "gal_text";
  const IMAGE = "gal_image";
  const COUNTDOWN = "gal_countdown";
  const GALLERY = "gal_gallery";
  const VIDEO = "gal_video";
  const TESTI = "gal_testimonials";
  const BLOG = "gal_blog";
  const FAQ = "gal_faq";
  const MAP = "gal_map";
  const DI = "gal_datainput";
  const CONTACT = "gal_contact";

  return enc({
    ...scaffold(
      { ab: "gal_ab", abs: "gal_abs", body: B, ft: "gal_ft", fts: "gal_fts" },
      [HERO, NUMBERS, DI, AGENDA, SPEAKER, TEXT, IMAGE, COUNTDOWN, GALLERY, VIDEO, TESTI, BLOG, FAQ, MAP, CONTACT],
      false, { background: "bg-black", padding: "p-4", borderRadius: "rounded-none" },
    ),

    // ── Hero (full-bleed dark image, light gold/cream text) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        sectionHeight: 92, contentAlignment: "center", imageLayout: "background", layoutWidth: "75%", inversed: false,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("galHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center", headlineColor: GOLD,
        isVisibleHeader: true, textHeader: td("galHeroHeader"), fontSizeHeader: "text-6xl", textAlignHeader: "center", headerColor: WHITE,
        isVisibleText: true, textText: td("galHeroText"), fontSizeText: "text-lg", textAlignText: "center", textColor: GOLD_SOFT,
        secondaryButtonSize: "medium", secondaryButtonText: td("galHeroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("galHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        backgroundType: "image", imageRounded: false, backgroundOverlayColor: "rgba(10,10,10,0.72)",
        imageSrc: `${W}/hero-einladung.jpg`, backgroundColor: BG,
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "gal_hero_eb", header: "gal_hero_hd", text: "gal_hero_tx", buttonOne: "gal_hero_b1", buttonTwo: "gal_hero_b2" },
    },
    gal_hero_eb: tc(HERO, { isVisible: true, text: td("galHeroEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: GOLD, header: true }),
    gal_hero_hd: tc(HERO, { isVisible: true, text: td("galHeroHeader"), fontSize: "text-6xl", textAlign: "center", displayName: "header", color: WHITE, header: true }),
    gal_hero_tx: tc(HERO, { isVisible: true, text: td("galHeroText"), fontSize: "text-lg", textAlign: "center", displayName: "text", color: GOLD_SOFT, header: false }),
    gal_hero_b1: btn(HERO, { type: "secondary", text: td("galHeroSecondaryButton"), displayName: "buttonOne" }),
    gal_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("galHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers (dark) ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%", backgroundColor: BG,
        isVisibleHeadline: true, textHeadline: td("galNumbersEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("galNumbersHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("galNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["gal_num1", "gal_num2", "gal_num3", "gal_num4"],
      linkedNodes: { headline: "gal_num_eb", header: "gal_num_hd", text: "gal_num_tx" },
    },
    gal_num_eb: tc(NUMBERS, { isVisible: true, text: td("galNumbersEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: GOLD, header: true }),
    gal_num_hd: tc(NUMBERS, { isVisible: true, text: td("galNumbersHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: WHITE, header: true }),
    gal_num_tx: tc(NUMBERS, { isVisible: true, text: td("galNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    gal_num1: numbersChild(NUMBERS, 0, td),
    gal_num2: numbersChild(NUMBERS, 1, td),
    gal_num3: numbersChild(NUMBERS, 2, td),
    gal_num4: numbersChild(NUMBERS, 3, td),

    // ── Agenda (dark surface) ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%", backgroundColor: SURFACE,
        isVisibleHeadline: true, textHeadline: td("galAgendaEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("galAgendaHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("galAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["gal_ag1", "gal_ag2", "gal_ag3", "gal_ag4", "gal_ag5"],
      linkedNodes: { headline: "gal_ag_eb", header: "gal_ag_hd", text: "gal_ag_tx" },
    },
    gal_ag_eb: tc(AGENDA, { isVisible: true, text: td("galAgendaEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: GOLD, header: true }),
    gal_ag_hd: tc(AGENDA, { isVisible: true, text: td("galAgendaHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: WHITE, header: true }),
    gal_ag_tx: tc(AGENDA, { isVisible: true, text: td("galAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    gal_ag1: agendaChild(AGENDA, 0, td),
    gal_ag2: agendaChild(AGENDA, 1, td),
    gal_ag3: agendaChild(AGENDA, 2, td),
    gal_ag4: agendaChild(AGENDA, 3, td),
    gal_ag5: agendaChild(AGENDA, 4, td),

    // ── Speaker (dark) — hidden by default (no portrait library) ──
    [SPEAKER]: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextSpeakers: "gap-8 wb-md:gap-12", distSpeakers: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%", backgroundColor: BG,
        isVisibleHeadline: true, textHeadline: td("galSpeakerEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("galSpeakerHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("galSpeakerText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["gal_sp1", "gal_sp2", "gal_sp3", "gal_sp4", "gal_sp5", "gal_sp6"],
      linkedNodes: { headline: "gal_sp_eb", header: "gal_sp_hd", text: "gal_sp_tx" },
    },
    gal_sp_eb: tc(SPEAKER, { isVisible: true, text: td("galSpeakerEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: GOLD, header: true }),
    gal_sp_hd: tc(SPEAKER, { isVisible: true, text: td("galSpeakerHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: WHITE, header: true }),
    gal_sp_tx: tc(SPEAKER, { isVisible: true, text: td("galSpeakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    gal_sp1: speakerChild(SPEAKER, 0, td),
    gal_sp2: speakerChild(SPEAKER, 1, td),
    gal_sp3: speakerChild(SPEAKER, 2, td),
    gal_sp4: speakerChild(SPEAKER, 3, td),
    gal_sp5: speakerChild(SPEAKER, 4, td),
    gal_sp6: speakerChild(SPEAKER, 5, td),

    // ── Text (editorial note from host, dark) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%", backgroundColor: SURFACE,
        isVisibleHeadline: true, textHeadline: td("galTextEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("galTextHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("galTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("galTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("galTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "gal_tx_eb", header: "gal_tx_hd", text: "gal_tx_tx", buttonOne: "gal_tx_b1", buttonTwo: "gal_tx_b2" },
    },
    gal_tx_eb: tc(TEXT, { isVisible: true, text: td("galTextEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: GOLD, header: true }),
    gal_tx_hd: tc(TEXT, { isVisible: true, text: td("galTextHeader"), fontSize: "text-4xl", textAlign: "center", displayName: "header", color: WHITE, header: true }),
    gal_tx_tx: tc(TEXT, { isVisible: true, text: td("galTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", color: CREAM, header: false }),
    gal_tx_b1: btn(TEXT, { type: "secondary", text: td("galTextSecondaryButton"), displayName: "buttonOne" }),
    gal_tx_b2: btn(TEXT, { type: "primary", text: td("galTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Image (tafel detail, image left / text right, dark) ──
    [IMAGE]: {
      type: { resolvedName: "Image" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, inversed: true,
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, backgroundColor: SURFACE,
        isVisibleHeadline: true, textHeadline: td("galImageEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("galImageHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("galImageText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("galImageButton"), isVisibleButton: true, buttonAlign: "left",
        buttonBorder: true, buttonUnderline: false,
        imageRounded: "rounded-none", layoutWidth: "50%",
        isVisibleImage: true, imageSrc: `${W}/detail-gold-cutlery.jpg`, imageAspectRatio: "aspect-[4/5]",
      },
      displayName: "Image", custom: { icon: "IconSection", preview: pv("Image-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "gal_im_eb", header: "gal_im_hd", text: "gal_im_tx", button: "gal_im_btn", image: "gal_im_img" },
    },
    gal_im_eb: tc(IMAGE, { isVisible: true, text: td("galImageEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: GOLD, header: true }),
    gal_im_hd: tc(IMAGE, { isVisible: true, text: td("galImageHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", color: WHITE, header: true }),
    gal_im_tx: tc(IMAGE, { isVisible: true, text: td("galImageText"), fontSize: "text-md", textAlign: "left", displayName: "text", color: CREAM, header: false }),
    gal_im_btn: btn(IMAGE, { type: "secondary", text: td("galImageButton"), displayName: "button" }),
    gal_im_img: {
      type: { resolvedName: "ImageChild" }, isCanvas: false,
      props: { src: `${W}/detail-gold-cutlery.jpg`, isVisible: true, aspectRatio: "aspect-[4/5]", displayName: "ImageChild", width: "w-full wb-lg:w-1/2", canDuplicate: false },
      displayName: "ImageChild", custom: { icon: "IconSection" },
      parent: IMAGE, hidden: false, nodes: [], linkedNodes: {},
    },

    // ── Countdown (dark) ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left", backgroundColor: SURFACE,
        isVisibleHeadline: true, textHeadline: td("galCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("galCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("galCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("galCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: SERIF, countdownFontColorDate: GOLD_SOFT,
        countdownSize: "text-base", countdownFontColor: GOLD, countdownBorderColor: "#2a2a2a",
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "gal_cd_eb", header: "gal_cd_hd", text: "gal_cd_tx", button: "gal_cd_btn" },
    },
    gal_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("galCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: GOLD, header: true }),
    gal_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("galCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: WHITE, header: true }),
    gal_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("galCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    gal_cd_btn: btn(COUNTDOWN, { text: td("galCountdownButton"), displayName: "ButtonChild" }),

    // ── Gallery (saal/tafel) — hidden by default (no photo library) ──
    [GALLERY]: {
      type: { resolvedName: "Gallery" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", distButtonGallery: "gap-3 wb-md:gap-4", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, buttonAlign: "center", gridColumns: "4", layoutWidth: "100%", backgroundColor: BG,
        isVisibleHeadline: true, textHeadline: td("galGalleryEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("galGalleryHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("galGalleryHeader"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("galGallerySecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("galGalleryPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
      },
      displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
      parent: B, hidden: false,
      nodes: ["gal_g1", "gal_g2", "gal_g3", "gal_g4", "gal_g5", "gal_g6"],
      linkedNodes: { headline: "gal_g_eb", header: "gal_g_hd", text: "gal_g_tx", secondary: "gal_g_b2", primary: "gal_g_b1" },
    },
    gal_g_eb: tc(GALLERY, { isVisible: true, text: td("galGalleryEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: GOLD, header: true }),
    gal_g_hd: tc(GALLERY, { isVisible: true, text: td("galGalleryHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: WHITE, header: true }),
    gal_g_tx: tc(GALLERY, { isVisible: false, text: td("galGalleryHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    gal_g_b2: btn(GALLERY, { type: "secondary", text: td("galGallerySecondaryButton"), displayName: "ButtonChild" }),
    gal_g_b1: btn(GALLERY, { text: td("galGalleryPrimaryButton"), displayName: "ButtonChild" }),
    gal_g1: galleryImage(GALLERY, 0),
    gal_g2: galleryImage(GALLERY, 1),
    gal_g3: galleryImage(GALLERY, 2),
    gal_g4: galleryImage(GALLERY, 3),
    gal_g5: galleryImage(GALLERY, 4),
    gal_g6: galleryImage(GALLERY, 5),

    // ── Video (recap) — present but hidden by default ──
    [VIDEO]: {
      type: { resolvedName: "Video" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentVideo: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false, backgroundColor: BG,
        isVisibleHeadline: true, textHeadline: td("galVideoEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("galVideoHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("galVideoText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("galVideoButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
        videoAspectRatio: "aspect-video", isVisibleVideo: true, videoUrl: "https://youtu.be/sdESKm-nKQE",
      },
      displayName: "Video", custom: { icon: "IconVideo", preview: pv("Video-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "gal_vd_eb", header: "gal_vd_hd", text: "gal_vd_tx", button: "gal_vd_btn" },
    },
    gal_vd_eb: tc(VIDEO, { isVisible: true, text: td("galVideoEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: GOLD, header: true }),
    gal_vd_hd: tc(VIDEO, { isVisible: true, text: td("galVideoHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: WHITE, header: true }),
    gal_vd_tx: tc(VIDEO, { isVisible: true, text: td("galVideoText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: CREAM, header: false }),
    gal_vd_btn: btn(VIDEO, { text: td("galVideoButton"), isVisible: true, displayName: "button" }),

    // ── Testimonials (dark surface) ──
    [TESTI]: {
      type: { resolvedName: "Testimonials" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextTestimonials: "gap-8 wb-md:gap-12", distTestimonials: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: true, gridColumns: "3", layoutWidth: "100%",
        backgroundColor: SURFACE,
        isVisibleHeadline: true, textHeadline: td("galTestimonialsEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("galTestimonialsHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("galTestimonialsHeader"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Testimonials", custom: { icon: "IconQuote", preview: pv("Testimonials-preview"), allowedComponents: ["TestimonialsChild"] },
      parent: B, hidden: false, nodes: ["gal_ts1", "gal_ts2", "gal_ts3"],
      linkedNodes: { headline: "gal_ts_eb", header: "gal_ts_hd", text: "gal_ts_tx" },
    },
    gal_ts_eb: tc(TESTI, { isVisible: true, text: td("galTestimonialsEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: GOLD, header: true }),
    gal_ts_hd: tc(TESTI, { isVisible: true, text: td("galTestimonialsHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: WHITE, header: true }),
    gal_ts_tx: tc(TESTI, { isVisible: false, text: td("galTestimonialsHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", color: CREAM, header: false }),
    gal_ts1: testimonialChild(TESTI, 0, td),
    gal_ts2: testimonialChild(TESTI, 1, td),
    gal_ts3: testimonialChild(TESTI, 2, td),

    // ── Blog (notizen) — present but hidden by default ──
    [BLOG]: {
      type: { resolvedName: "Blog" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%", backgroundColor: BG,
        isVisibleHeadline: true, textHeadline: td("galBlogEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("galBlogHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("galBlogText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Blog", custom: { icon: "IconClick", preview: pv("Blog-preview"), allowedComponents: ["BlogChild"] },
      parent: B, hidden: false, nodes: ["gal_bl1", "gal_bl2", "gal_bl3"],
      linkedNodes: { headline: "gal_bl_eb", header: "gal_bl_hd", text: "gal_bl_tx" },
    },
    gal_bl_eb: tc(BLOG, { isVisible: true, text: td("galBlogEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: GOLD, header: true }),
    gal_bl_hd: tc(BLOG, { isVisible: true, text: td("galBlogHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: WHITE, header: true }),
    gal_bl_tx: tc(BLOG, { isVisible: true, text: td("galBlogText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    gal_bl1: blogChild(BLOG, 0, td),
    gal_bl2: blogChild(BLOG, 1, td),
    gal_bl3: blogChild(BLOG, 2, td),

    // ── FaQ (dark surface) ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%", backgroundColor: SURFACE,
        isVisibleHeadline: true, textHeadline: td("galFaqEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("galFaqHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("galFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["gal_fq1", "gal_fq2", "gal_fq3", "gal_fq4", "gal_fq5", "gal_fq6"],
      linkedNodes: { headline: "gal_fq_eb", header: "gal_fq_hd", text: "gal_fq_tx" },
    },
    gal_fq_eb: tc(FAQ, { isVisible: true, text: td("galFaqEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: GOLD, header: true }),
    gal_fq_hd: tc(FAQ, { isVisible: true, text: td("galFaqHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: WHITE, header: true }),
    gal_fq_tx: tc(FAQ, { isVisible: true, text: td("galFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    gal_fq1: faqChild(FAQ, 0, td),
    gal_fq2: faqChild(FAQ, 1, td),
    gal_fq3: faqChild(FAQ, 2, td),
    gal_fq4: faqChild(FAQ, 3, td),
    gal_fq5: faqChild(FAQ, 4, td),
    gal_fq6: faqChild(FAQ, 5, td),

    // ── Map (venue) — present but hidden by default ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false, backgroundColor: BG,
        isVisibleHeadline: true, textHeadline: td("galMapEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("galMapHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("galMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("galMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-none",
        mapZoom: 14, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("galMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "gal_mp_eb", header: "gal_mp_hd", text: "gal_mp_tx", button: "gal_mp_btn" },
    },
    gal_mp_eb: tc(MAP, { isVisible: true, text: td("galMapEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: GOLD, header: true }),
    gal_mp_hd: tc(MAP, { isVisible: true, text: td("galMapHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", color: WHITE, header: true }),
    gal_mp_tx: tc(MAP, { isVisible: true, text: td("galMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", color: CREAM, header: false }),
    gal_mp_btn: btn(MAP, { text: td("galMapButton"), displayName: "button" }),

    // ── DataInput (multi-step RSVP) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: SURFACE, border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("galDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("galDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("galDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: true, stepLabels: [td("galStepCouvert"), td("galStepProfile"), td("galStepMenu"), td("galStepOverview")], multiStepMode: "pages",
        progressBarEnabled: true, progressBarColor: GOLD,
        overviewEnabled: true, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "gal_di_hl", header: "gal_di_hd", text: "gal_di_tx",
        fieldsGroup0: "gal_di_f0", fieldsGroup1: "gal_di_f1", fieldsGroup2: "gal_di_f2",
        fieldsGroup3: "gal_di_f3", fieldsGroup4: "gal_di_f4",
        companionsGroup: "gal_di_comp", overviewGroup: "gal_di_ov", checkboxes: "gal_di_cb",
        button: "gal_di_btn", backButton: "gal_di_back", nextButton: "gal_di_next",
      },
    },
    gal_di_hl: tc(DI, { isVisible: true, text: td("galDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    gal_di_hd: tc(DI, { isVisible: true, text: td("galDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    gal_di_tx: tc(DI, { isVisible: true, text: td("galDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    gal_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 4, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["gal_di_acceptance", "gal_di_first", "gal_di_last", "gal_di_email"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    gal_di_acceptance: acceptanceFi("gal_di_f0", td("acceptanceState")),
    gal_di_first: fi("gal_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    gal_di_last: fi("gal_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    gal_di_email: fi("gal_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    gal_di_f1: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 2, groupIndex: 1, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["gal_di_salutation", "gal_di_menu"], linkedNodes: {},
    },
    gal_di_salutation: fi("gal_di_f1", { label: td("galFieldSalutation"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "salutation", changeable: true }),
    gal_di_menu: fi("gal_di_f1", { label: td("galFieldMenu"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "menuLine", changeable: true }),
    gal_di_f2: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, groupIndex: 2, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["gal_di_allergies"], linkedNodes: {},
    },
    gal_di_allergies: fi("gal_di_f2", { label: td("galFieldAllergies"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "allergies", multiline: true, gridArea: "col-span-12", changeable: true }),
    gal_di_f3: emptyFieldsGroup(DI, 3),
    gal_di_f4: emptyFieldsGroup(DI, 4),
    gal_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: false, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["gal_di_comp_first"], linkedNodes: {},
    },
    gal_di_comp_first: fi("gal_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    gal_di_ov_t: tc("gal_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    gal_di_ov_x: tc("gal_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    gal_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["gal_di_ov_t", "gal_di_ov_x"], linkedNodes: {},
    },
    gal_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    gal_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("galDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    gal_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    gal_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Contact (concierge) ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: BG,
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("galContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("galContactHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("galContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "linkedin", url: "https://linkedin.com" }, { platform: "instagram", url: "https://instagram.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("galContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "gal_ct_eb", header: "gal_ct_hd", text: "gal_ct_tx",
        firstName: "gal_ct_first", lastName: "gal_ct_last", eMail: "gal_ct_email",
        phoneNumber: "gal_ct_phone", Message: "gal_ct_msg", button: "gal_ct_btn",
      },
    },
    gal_ct_eb: tc(CONTACT, { isVisible: true, text: td("galContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: GOLD, header: true }),
    gal_ct_hd: tc(CONTACT, { isVisible: true, text: td("galContactHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", color: WHITE, header: true }),
    gal_ct_tx: tc(CONTACT, { isVisible: true, text: td("galContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", color: CREAM, header: false }),
    gal_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    gal_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    gal_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    gal_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    gal_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    gal_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("galContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────

/** Dark/transparent bar, gold bottom rule, logo + light nav + gold-hairline CTA. */
export const galaAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      backgroundColor: BG, transparent: true,
      border: true, borderColor: GOLD, borderWidth: "border", borderStyle: "border-solid", borderSides: ["bottom"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("galLinkProgram"), url: "#gal_agenda", linkType: "internal" },
        { label: td("galLinkHonor"), url: "#gal_speaker", linkType: "internal" },
        { label: td("galLinkFaq"), url: "#gal_faq", linkType: "internal" },
        { label: td("galLinkLocation"), url: "#gal_contact", linkType: "internal" },
      ],
      linksColor: CREAM, linksFontSize: "text-sm", linksFontWeight: "font-medium",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("galHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: "transparent", logoutButtonColor: GOLD, logoutButtonBorderColor: GOLD,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Near-black footer, gold top rule, four link groups + social + copyright. No logo. */
export const galaFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: BG,
      // border-4 + borderSides:["top"] → getBorderClasses emits "border-t-4"
      // (passing "border-t-4" as the width would mis-emit "border-t-t-4").
      border: true, borderColor: GOLD, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      // No footer logo (per design) — omit "logo" from sectionOrder + hide it.
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("galFooterEvening"), links: [
          { label: td("galLinkProgram"), url: "#gal_agenda", linkType: "internal" },
          { label: td("galLinkHonor"), url: "#gal_speaker", linkType: "internal" },
          { label: td("galLinkConcierge"), url: "/login", linkType: "internal" },
        ] },
        // Event practicalities — Maps + add-to-calendar are EXTERNAL (new tab).
        // URLs are placeholders the organiser edits per event.
        { title: td("galFooterEvent"), links: [
          { label: td("galLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Opernplatz+1+Frankfurt", linkType: "external" },
          { label: td("galLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Benefiz-Gala+MMXXVI", linkType: "external" },
          { label: td("galLinkReserve"), url: "/register", linkType: "internal" },
        ] },
        { title: td("galFooterSupport"), links: [
          { label: td("galLinkContact"), url: "#gal_contact", linkType: "internal" },
          { label: td("galLinkFaq"), url: "#gal_faq", linkType: "internal" },
          { label: td("galLinkPress"), url: "#gal_contact", linkType: "internal" },
        ] },
        { title: td("galFooterLegal"), links: [
          { label: td("galLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("galLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("galLinkTerms"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: GOLD_SOFT, linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: MUTE, linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "linkedin", platform: "linkedin", url: "https://linkedin.com" },
        { altText: "instagram", platform: "instagram", url: "https://instagram.com" },
      ],
      socialColor: MUTE, socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#a0978a", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });

// ── Theme CSS (P9 WS4-B1) ─────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3).
// The hero photo + dark scrim stay node props (backgroundOverlayColor); the
// css layers the engraved-invitation poster treatment on top: a gold hairline
// ring ornament, a formal gold rule, uppercase letterspaced kickers, and the
// self-hosted faces so the preset's Didot/Inter stacks actually resolve.
const CUSTOM_CSS = `/* theme:gala */
${fontFaces([
  { family: "Didot", file: "bodoni-moda", weightRange: "400 900" },
  { family: "Inter", file: "inter-regular", weightRange: "400" },
  { family: "Inter", file: "inter-bold", weightRange: "700" },
])}

/* Ballroom hero — clip + isolate the decorative ornament layers */
#gal_hero {
${heroShell}
}

/* Engraved-invitation motif: a gold hairline ring floating over the scrim
   (decorLayer's z-index 1 lifts it above the overlay div, still under the
   z-10 content) */
#gal_hero::before {
${decorLayer}
  width: 340px;
  height: 340px;
  top: 50%;
  left: 50%;
  margin: -170px 0 0 -170px;
  border: 1px solid rgba(201, 162, 75, 0.4);
  border-radius: 50%;
}

/* Formal gold rule beneath the headline block */
#gal_hero::after {
${decorLayer}
  left: 50%;
  bottom: 11%;
  width: 120px;
  height: 1px;
  margin-left: -60px;
  background: ${GOLD};
}

/* Uppercase letterspaced kickers (the text-xs eyebrows in each section) */
#gal_hero .text-xs, #gal_numbers .text-xs, #gal_agenda .text-xs, #gal_speaker .text-xs,
#gal_text .text-xs, #gal_image .text-xs, #gal_countdown .text-xs, #gal_gallery .text-xs,
#gal_video .text-xs, #gal_testimonials .text-xs, #gal_blog .text-xs, #gal_faq .text-xs,
#gal_map .text-xs, #gal_contact .text-xs {
  text-transform: uppercase;
  letter-spacing: 0.22em;
}

${motionGate(`${fadeUpKeyframes("gal", 18)}
  @keyframes galOrnamentIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes galOrnamentFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-9px); }
  }

  /* Candlelit entrance: the poster settles, the gold ring fades in and floats */
  #gal_hero { animation: galFadeUp 0.9s ${EASE} both; }
  #gal_hero::before {
    animation: galOrnamentIn 1.8s ${EASE} 0.4s both, galOrnamentFloat 13s ease-in-out 2.2s infinite;
  }

${revealBlock("gal", ["numbers", "datainput", "agenda", "speaker", "text", "image", "countdown", "gallery", "video", "testimonials", "blog", "faq", "map", "contact"], 30)}`)}
`;

// ── Style preset (dark-luxury-gold) ───────────────────────────────────

export const galaPreset: WebsiteThemeStylePreset = {
  heroImage: `${W}/hero-einladung.jpg`,
  logoSrc: LOGO,
  colorPalette: { primary: GOLD, secondary: GOLD_SOFT, background: BG },
  fonts: { header: SERIF, body: SANS },
  rounding: "rounded-none",
  recommendedSize: "large",
  bodyTheme: { fontColor: CREAM, fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    h1: { fontFamily: SERIF, fontSize: "48px", fontColor: WHITE },
    h2: { fontFamily: SERIF, fontSize: "28px", fontColor: GOLD_SOFT },
  },
  buttonsTheme: {
    // Primary stays SOLID on hover (gold → pale champagne), near-black text —
    // high-contrast CTA per P9 (no ghost flip on the primary action).
    primary: { bgColor: GOLD, bgHoverColor: GOLD_SOFT, borderColor: GOLD, borderHoverColor: GOLD_SOFT, fontColor: BG, fontHoverColor: BG, fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-none", hoverBorderRadius: "rounded-none" },
    secondary: { bgColor: "transparent", bgHoverColor: GOLD, borderColor: GOLD, borderHoverColor: GOLD, fontColor: GOLD, fontHoverColor: BG, fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-none", hoverBorderRadius: "rounded-none" },
  },
  hyperlinkTheme: { fontColor: GOLD, fontHoverColor: GOLD_SOFT, underline: false },
  formInputTheme: { labelFontColor: CREAM, inputFontColor: CREAM, backgroundColor: BG, border: true, borderColor: "#2a2a2a", borderRadius: "rounded-none", borderStyle: "border-solid", borderWidth: "border", borderSides: [], fontFamily: SANS, fontSize: "text-sm", errorColor: "#DC2626" },
  customCss: CUSTOM_CSS,
};
